import React from "react";
import { Badge } from "@src/common/components";
import { PackageDisplayItem, PackageEventDisplayItem } from "./types";
import { PackageStatus } from "@api/graphql/generated/generated-types";
import { FlowbiteColors } from "flowbite-react";
import moment from "moment";

interface PackageEventsDisplayProps {
  packages: PackageDisplayItem[];
}

function getStatusColor(status: PackageStatus): keyof FlowbiteColors {
  switch (status) {
    case PackageStatus.Delivered:
      return "success";
    case PackageStatus.Driver:
      return "info";
    case PackageStatus.Warehouse:
      return "warning";
    case PackageStatus.Created:
      return "gray";
    default:
      return "gray";
  }
}

function formatLocation(location: { lat: number; lng: number } | null): string {
  if (!location) return "-";
  return `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`;
}

function formatTime(createdAt: string, deviceTime: string | null): React.ReactNode {
  const formattedCreatedAt = moment(createdAt).format("MMM D, h:mm a");
  
  if (deviceTime) {
    const formattedDeviceTime = moment(deviceTime).format("h:mm a");
    return (
      <div className="flex flex-col">
        <span>{formattedCreatedAt}</span>
        <span className="text-gray-500 text-xs">Device: {formattedDeviceTime}</span>
      </div>
    );
  }
  
  return formattedCreatedAt;
}

function PackageEventsTable({ events }: { events: PackageEventDisplayItem[] }) {
  const sortedEvents = [...events].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-slate-50 text-left">
          <th className="px-3 py-2 font-semibold text-gray-700">Event Type</th>
          <th className="px-3 py-2 font-semibold text-gray-700">Action</th>
          <th className="px-3 py-2 font-semibold text-gray-700">Status</th>
          <th className="px-3 py-2 font-semibold text-gray-700">Time</th>
          <th className="px-3 py-2 font-semibold text-gray-700">Scanned By</th>
          <th className="px-3 py-2 font-semibold text-gray-700">Location</th>
        </tr>
      </thead>
      <tbody>
        {sortedEvents.map((event) => (
          <tr key={event.id} className="border-b border-gray-100 hover:bg-gray-50">
            <td className="px-3 py-2">{event.eventTypeName}</td>
            <td className="px-3 py-2">
              <span className="text-xs font-medium text-gray-600">
                {event.action.replace(/_/g, " ")}
              </span>
            </td>
            <td className="px-3 py-2">
              <Badge color={getStatusColor(event.packageStatus)} size="xs">
                {event.packageStatus.replace(/_/g, " ")}
              </Badge>
            </td>
            <td className="px-3 py-2">{formatTime(event.createdAt, event.deviceTime)}</td>
            <td className="px-3 py-2">{event.scannedByDriverName || "-"}</td>
            <td className="px-3 py-2 text-xs text-gray-500">
              {formatLocation(event.location)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function PackageEventsDisplay({ packages }: PackageEventsDisplayProps) {
  if (packages.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No packages found for this shipment.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {packages.map((pkg) => (
        <div key={pkg.id} className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">Package:</span>
              <span className="text-gray-700">{pkg.reference}</span>
            </div>
            <Badge color={getStatusColor(pkg.status)}>
              {pkg.status.replace(/_/g, " ")}
            </Badge>
          </div>
          {pkg.events.length > 0 ? (
            <PackageEventsTable events={pkg.events} />
          ) : (
            <div className="text-center text-gray-500 py-4">
              No events recorded for this package.
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

